import moment from 'moment';
import {
    createSelector,
    lruMemoize,
} from 'reselect';

import NAMESPACE from './types';
import SLICE_NAMESPACE from '../../types';

function _dataSelector(state) {
    return state[SLICE_NAMESPACE]
        ? state[SLICE_NAMESPACE][NAMESPACE]
        : undefined;
}

function _providerSelector(data) {
    return data.provider;
}

function _userBillingAddressSelector(data) {
    const {
        billingAddress = {},
    } = data.userInfo || {};
    return {
        street: billingAddress.street,
        street2: billingAddress.street2,
        city: billingAddress.city,
        stateProvince: billingAddress.stateProvince,
        country: billingAddress.country,
        phone: billingAddress.phone,
        postalCode: billingAddress.postalCode,
    };
}

function _userHasAccount(data) {
    return data?.hasAccount;
}

function _userInfoSelector(data = {}) {
    const {
        userInfo = {},
        loyaltyInfo = {},
    } = data;
    const {
        referral,
        points,
    } = loyaltyInfo;

    return {
        ...userInfo,
        email: userInfo.email,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        availablePoints: points?.available,
        pendingPoints: points?.pending,
        referralCode: referral?.code,
    };
}

function _userLoggedInSelector(data) {
    return data?.isLoggedIn;
}

function _allTripsSelector(data) {
    return data.orders || [];
}

function _tripByIdsSelector(allTrips, tripId, processingId) {
    return allTrips.find((trip) => {
        const sameId = trip.tripDetails.tripId === tripId;
        const sameProcessingId = trip.processingId === processingId;
        return sameId && sameProcessingId;
    });
}

function _pastTripsSelector(data) {
    const trips = data.orders || [];

    return trips
        .filter((order) => {
            const { departDateTimeUTC } = order.tripDetails;
            return moment.unix(departDateTimeUTC).isBefore(moment());
        })
        .sort((orderA, orderB) => {
            const departTimeA = orderA.tripDetails.departDateTimeUTC;
            const departTimeB = orderB.tripDetails.departDateTimeUTC;
            return departTimeB - departTimeA;
        });
}

function _upcomingTripsSelector(data) {
    const trips = data.orders || [];

    return trips
        .filter((order) => {
            const { departDateTimeUTC } = order.tripDetails;
            return moment.unix(departDateTimeUTC).isSameOrAfter(moment());
        })
        .sort((orderA, orderB) => {
            const departTimeA = orderA.tripDetails.departDateTimeUTC;
            const departTimeB = orderB.tripDetails.departDateTimeUTC;
            return departTimeA - departTimeB;
        });
}

const selectProvider = createSelector(
    _dataSelector,
    _providerSelector,
);

const selectUserBillingAddress = createSelector(
    _dataSelector,
    _userBillingAddressSelector,
);

const selectUserHasAccount = createSelector(
    _dataSelector,
    _userHasAccount,
);

const selectUserInfo = createSelector(
    _dataSelector,
    _userInfoSelector,
);

const selectLoyaltyInfo = createSelector(
    _dataSelector,
    ({ loyaltyInfo = {} }) => loyaltyInfo,
);

const selectIdentityId = createSelector(
    _dataSelector,
    (data) => data?.identityId,
);

const selectReferralCode = createSelector(
    selectLoyaltyInfo,
    ({ referral }) => referral?.code,
);

const selectUserLoggedIn = createSelector(
    _dataSelector,
    _userLoggedInSelector,
);

const selectAllTrips = createSelector(
    _dataSelector,
    _allTripsSelector,
);

const selectTripByIds = lruMemoize((appState, tripId, processingId) => {
    return createSelector(
        selectAllTrips,
        (allTrips) => _tripByIdsSelector(allTrips, tripId, processingId),
    )(appState);
});

const selectIsNewAccount = createSelector(
    _dataSelector,
    (data) => data.isNewAccount,
);

// Time-based selectors should not be cached
const selectPastTrips = (appState) => {
    return _pastTripsSelector(_dataSelector(appState));
};

const selectUpcomingTrips = (appState) => {
    return _upcomingTripsSelector(_dataSelector(appState));
};

export {
    selectAllTrips,
    selectIdentityId,
    selectIsNewAccount,
    selectLoyaltyInfo,
    selectPastTrips,
    selectProvider,
    selectReferralCode,
    selectTripByIds,
    selectUpcomingTrips,
    selectUserBillingAddress,
    selectUserHasAccount,
    selectUserInfo,
    selectUserLoggedIn,
};
